import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';

const HTMLMobileFirst = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Responsive Design</div>
                <h1>Mobile-First Development</h1>
                <p className="hero-lead">
                    Learn the mobile-first methodology for building modern responsive websites. Start with mobile
                    design and progressively enhance for larger screens.
                </p>
            </div>

            <div className="content-section">
                <h2>What is Mobile-First?</h2>
                <p>
                    Mobile-first is a design philosophy where you design and build for mobile devices first,
                    then add features and layouts for larger screens using media queries.
                </p>

                <div className="comparison-grid">
                    <div className="comparison-card error">
                        <h3> Desktop-First (Old Way)</h3>
                        <ol>
                            <li>Design for desktop (1920px)</li>
                            <li>Remove features for tablets</li>
                            <li>Remove more features for mobile</li>
                            <li>Result: Clunky mobile experience</li>
                        </ol>
                    </div>
                    <div className="comparison-card">
                        <h3>Mobile-First (Modern Way)</h3>
                        <ol>
                            <li>Design for mobile (375px)</li>
                            <li>Add features for tablets</li>
                            <li>Add more features for desktop</li>
                            <li>Result: Great experience on all devices</li>
                        </ol>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Why Mobile-First?</h2>

                <div className="benefits-list">
                    <h3>Performance Benefits</h3>
                    <ul>
                        <li>Load only what's needed for mobile (faster)</li>
                        <li>Progressive enhancement for larger screens</li>
                        <li>Smaller initial CSS download</li>
                        <li>Better for slow mobile networks</li>
                    </ul>

                    <h3>User Experience Benefits</h3>
                    <ul>
                        <li>Forces focus on essential content</li>
                        <li>Touch-friendly by design</li>
                        <li>Better mobile usability</li>
                        <li>Consistent experience across devices</li>
                    </ul>

                    <h3>Development Benefits</h3>
                    <ul>
                        <li>Simpler CSS (adding vs. removing)</li>
                        <li>Easier to maintain</li>
                        <li>Better performance by default</li>
                        <li>Future-proof (mobile traffic growing)</li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>Mobile-First CSS</h2>

                <h3> Desktop-First Approach</h3>
                <div className="code-example error">
                    <CodeBlock language="css" code={`/* Desktop styles (default) */
.container {
    width: 1200px;
    display: grid;
    grid-template-columns: repeat(4, 1fr);
}

/* Remove columns for tablet */
@media (max-width: 1024px) {
    .container {
        width: 100%;
        grid-template-columns: repeat(2, 1fr);
    }
}

/* Remove more for mobile */
@media (max-width: 768px) {
    .container {
        grid-template-columns: 1fr;
    }
}

/* Problems:
   - Mobile devices load desktop CSS first
   - Overriding is complex
   - Performance impact on mobile --&gt;`} />
                </div>

                <h3>Mobile-First Approach</h3>
                <div className="code-example">
                    <CodeBlock language="css" code={`/* Mobile styles (default) */
.container {
    width: 100%;
    display: grid;
    grid-template-columns: 1fr;
}

/* Add columns for tablet */
@media (min-width: 768px) {
    .container {
        grid-template-columns: repeat(2, 1fr);
    }
}

/* Add more columns for desktop */
@media (min-width: 1024px) {
    .container {
        max-width: 1200px;
        margin: 0 auto;
        grid-template-columns: repeat(4, 1fr);
    }
}

/* Benefits:
   - Mobile gets simple CSS first
   - Progressive enhancement
   - Better performance --&gt;`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Mobile-First Layout Example</h2>

                <div className="code-example">
                    <CodeBlock language="html" code={`<!DOCTYPE html>
<html>
<head>
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <style>
        * {
            box-sizing: border-box;
            margin: 0;
            padding: 0;
        }
        
        /* === MOBILE FIRST (Base Styles) === */
        body {
            font-family: Arial, sans-serif;
            font-size: 16px;
            line-height: 1.6;
            padding: 10px;
        }
        
        header {
            background: #333;
            color: white;
            padding: 15px;
            margin-bottom: 20px;
        }
        
        nav ul {
            list-style: none;
        }
        
        nav li {
            margin: 10px 0;
        }
        
        nav a {
            color: white;
            text-decoration: none;
            display: block;
            padding: 10px;
            background: #444;
        }
        
        main {
            padding: 15px;
        }
        
        .card {
            background: #f5f5f5;
            padding: 20px;
            margin-bottom: 20px;
            border-radius: 8px;
        }
        
        img {
            max-width: 100%;
            height: auto;
        }
        
        /* === TABLET (768px+) === */
        @media (min-width: 768px) {
            body {
                padding: 20px;
            }
            
            nav ul {
                display: flex;
                gap: 10px;
            }
            
            nav li {
                margin: 0;
            }
            
            .grid {
                display: grid;
                grid-template-columns: repeat(2, 1fr);
                gap: 20px;
            }
        }
        
        /* === DESKTOP (1024px+) === */
        @media (min-width: 1024px) {
            body {
                max-width: 1200px;
                margin: 0 auto;
            }
            
            .layout {
                display: grid;
                grid-template-columns: 250px 1fr;
                gap: 30px;
            }
            
            .grid {
                grid-template-columns: repeat(3, 1fr);
            }
        }
    </style>
</head>
<body>
    <header>
        <h1>Mobile-First Site</h1>
        <nav>
            <ul>
                <li><a href="#home">Home</a></li>
                <li><a href="#about">About</a></li>
                <li><a href="#services">Services</a></li>
                <li><a href="#contact">Contact</a></li>
            </ul>
        </nav>
    </header>
    
    <div class="layout">
        <aside>
            <h2>Sidebar</h2>
            <p>Sidebar content</p>
        </aside>
        
        <main>
            <h1>Main Content</h1>
            <div class="grid">
                <div class="card">Card 1</div>
                <div class="card">Card 2</div>
                <div class="card">Card 3</div>
                <div class="card">Card 4</div>
                <div class="card">Card 5</div>
                <div class="card">Card 6</div>
            </div>
        </main>
    </div>
</body>
</html>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Mobile-First Typography</h2>

                <div className="code-example">
                    <CodeBlock language="css" code={`/* Mobile base (16px) */
body {
    font-size: 16px;
    line-height: 1.5;
}

h1 { font-size: 1.75rem; }  /* 28px */
h2 { font-size: 1.5rem; }   /* 24px */
h3 { font-size: 1.25rem; }  /* 20px */
p { margin-bottom: 1rem; }

/* Tablet: slightly larger */
@media (min-width: 768px) {
    body { font-size: 18px; }
    h1 { font-size: 2rem; }
    h2 { font-size: 1.75rem; }
}

/* Desktop: larger still */
@media (min-width: 1024px) {
    body { font-size: 18px; }
    h1 { font-size: 2.5rem; }
    h2 { font-size: 2rem; }
}

/* Or use fluid typography */
h1 {
    font-size: clamp(1.75rem, 4vw, 3rem);
}`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Mobile-First Images</h2>

                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Load small image by default (mobile) --&gt;
<img src="image-small-400w.jpg"
     srcset="image-small-400w.jpg 400w,
             image-medium-800w.jpg 800w,
             image-large-1200w.jpg 1200w"
     sizes="(min-width: 1024px) 1200px,
            (min-width: 768px) 800px,
            400px"
     alt="Responsive image">

<!-- Browser automatically loads:
   - 400w image on mobile
   - 800w image on tablet
   - 1200w image on desktop --&gt;`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Mobile-First JavaScript</h2>

                <h3>Load Features Conditionally</h3>
                <div className="code-example">
                    <CodeBlock language="javascript" code={`// Only load heavy features on desktop
if (window.innerWidth >= 1024) {
    // Load parallax scrolling
    loadParallax();
    
    // Load animations
    loadAnimations();
}

// Feature detection
if ('IntersectionObserver' in window) {
    // Use modern API
    setupLazyLoading();
} else {
    // Fallback for older browsers
    loadAllImages();
}

// Touch vs. mouse
if ('ontouchstart' in window) {
    // Touch device - use touch events
    setupTouchGestures();
} else {
    // Desktop - use mouse events
    setupMouseHovers();
}`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Mobile-First Navigation</h2>

                <div className="code-example">
                    <CodeBlock language="html" code={`<style>
/* Mobile: Hamburger menu */
.nav-toggle {
    display: block;
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
}

.nav-menu {
    display: none;
    flex-direction: column;
}

.nav-menu.active {
    display: flex;
}

/* Desktop: Always show menu */
@media (min-width: 768px) {
    .nav-toggle {
        display: none;
    }
    
    .nav-menu {
        display: flex;
        flex-direction: row;
    }
}
</style>

<nav>
    <button class="nav-toggle" onclick="toggleMenu()"></button>
    <ul class="nav-menu" id="navMenu">
        <li><a href="#home">Home</a></li>
        <li><a href="#about">About</a></li>
        <li><a href="#services">Services</a></li>
        <li><a href="#contact">Contact</a></li>
    </ul>
</nav>

<script>
function toggleMenu() {
    document.getElementById('navMenu').classList.toggle('active');
}
</script>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Testing Mobile-First</h2>

                <h3>Test in This Order:</h3>
                <ol>
                    <li><strong>Mobile (320px - 375px):</strong> Start here, test everything</li>
                    <li><strong>Phablet (414px - 480px):</strong> Larger phones</li>
                    <li><strong>Tablet Portrait (768px):</strong> Test enhanced features</li>
                    <li><strong>Tablet Landscape (1024px):</strong> Test grid layouts</li>
                    <li><strong>Desktop (1280px - 1920px):</strong> Test full experience</li>
                </ol>

                <h3>Real Device Testing</h3>
                <ul>
                    <li>Test on actual phones and tablets</li>
                    <li>Test different browsers (Safari, Chrome, Firefox)</li>
                    <li>Test slow 3G connections</li>
                    <li>Test touch interactions</li>
                    <li>Test in bright sunlight (contrast)</li>
                </ul>
            </div>

            <div className="content-section">
                <h2>Mobile-First Checklist</h2>

                <div className="checklist">
                    <h3>Content</h3>
                    <ul>
                        <li> Focus on essential content first</li>
                        <li> Prioritize above-the-fold content</li>
                        <li> Keep mobile content concise</li>
                        <li> Use progressive disclosure</li>
                    </ul>

                    <h3>Design</h3>
                    <ul>
                        <li> Design mobile layout first</li>
                        <li> Use single-column layout as base</li>
                        <li> Touch targets 48x48px minimum</li>
                        <li> Adequate spacing between elements</li>
                        <li> Readable font sizes (16px+)</li>
                    </ul>

                    <h3>Code</h3>
                    <ul>
                        <li> Write mobile CSS first (no media query)</li>
                        <li> Use min-width media queries</li>
                        <li> Load mobile images first</li>
                        <li> Lazy load below-the-fold content</li>
                        <li> Defer non-critical JavaScript</li>
                    </ul>

                    <h3>Performance</h3>
                    <ul>
                        <li> Optimize for 3G networks</li>
                        <li> Minimize initial page weight</li>
                        <li> Use image compression</li>
                        <li> Minimize render-blocking resources</li>
                        <li> Test with Chrome Lighthouse</li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>Common Mistakes to Avoid</h2>

                <div className="mistakes-list">
                    <h3> Hiding Content on Mobile</h3>
                    <p>If content is important, show it on mobile too (maybe differently).</p>

                    <h3> Using max-width Media Queries</h3>
                    <p>Use min-width for mobile-first approach.</p>

                    <h3> Testing Only in DevTools</h3>
                    <p>Always test on real devices - touch, performance, and network differ.</p>

                    <h3> Ignoring Performance</h3>
                    <p>Mobile users often have slow connections - optimize aggressively.</p>

                    <h3> Assuming High-Speed Internet</h3>
                    <p>Test on 3G or slower to understand real mobile experience.</p>
                </div>
            </div>

            <div className="content-section">
                <h2>Mobile-First Frameworks</h2>

                <div className="frameworks-list">
                    <h3>CSS Frameworks (Mobile-First)</h3>
                    <ul>
                        <li><strong>Bootstrap 5:</strong> Mobile-first grid and components</li>
                        <li><strong>Tailwind CSS:</strong> Utility-first, mobile-first responsive design</li>
                        <li><strong>Bulma:</strong> Modern mobile-first CSS framework</li>
                    </ul>

                    <h3>Best Practices from Frameworks</h3>
                    <ul>
                        <li>Use flexible grids</li>
                        <li>Use relative units</li>
                        <li>Mobile-first breakpoints</li>
                        <li>Component-based design</li>
                    </ul>
                </div>
            </div>

        </div>
    );
};

export default HTMLMobileFirst;




